/**
 * JsonArtifactToolbar — copy/download actions for structured demo artifacts.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useMemo } from "react";
import { Download } from "lucide-react";
import { GRAY, PRIMARY, SAFE } from "../design-system/tokens.js";
import { downloadJsonFile } from "../utils/downloadFile.js";
import { CopyButton } from "./CopyButton.js";

export interface JsonArtifactToolbarProps {
  readonly artifact: unknown;
  readonly filename: string;
  readonly ariaLabel?: string;
  readonly copyLabel?: string;
  readonly downloadLabel?: string;
  readonly tone?: "light" | "dark";
}

interface ToneStyle {
  readonly border: string;
  readonly background: string;
  readonly color: string;
  readonly downloadBackground: string;
  readonly downloadBorder: string;
  readonly downloadColor: string;
}

const TONE_STYLE: Readonly<Record<NonNullable<JsonArtifactToolbarProps["tone"]>, ToneStyle>> = {
  light: {
    border: GRAY[300],
    background: "white",
    color: GRAY[600],
    downloadBackground: PRIMARY[600],
    downloadBorder: PRIMARY[600],
    downloadColor: "white",
  },
  dark: {
    border: "rgba(148, 163, 184, 0.24)",
    background: "rgba(15, 23, 42, 0.58)",
    color: GRAY[300],
    downloadBackground: "rgba(56, 189, 248, 0.14)",
    downloadBorder: "rgba(56, 189, 248, 0.38)",
    downloadColor: SAFE[100],
  },
};

function prettyJson(value: unknown): string {
  return JSON.stringify(value, null, 2);
}

export function JsonArtifactToolbar({
  artifact,
  filename,
  ariaLabel = "Artifact actions",
  copyLabel = "Copy JSON",
  downloadLabel = "Download JSON",
  tone = "light",
}: JsonArtifactToolbarProps): React.JSX.Element {
  const json = useMemo(() => prettyJson(artifact), [artifact]);
  const styles = TONE_STYLE[tone];

  return (
    <div
      role="toolbar"
      aria-label={ariaLabel}
      style={{
        alignItems: "center",
        background: styles.background,
        border: `1px solid ${styles.border}`,
        borderRadius: 8,
        color: styles.color,
        display: "flex",
        flexWrap: "wrap",
        gap: 8,
        justifyContent: "space-between",
        padding: "0.5rem",
      }}
    >
      <span
        style={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: "0.04em",
          minWidth: 0,
          overflowWrap: "anywhere",
          textTransform: "uppercase",
        }}
      >
        {filename}
      </span>
      <span style={{ alignItems: "center", display: "inline-flex", gap: 8 }}>
        <CopyButton value={json} idleLabel={copyLabel} copiedLabel="Copied" />
        <button
          type="button"
          aria-label={`${downloadLabel}: ${filename}`}
          onClick={() => {
            downloadJsonFile(filename, artifact);
          }}
          style={{
            alignItems: "center",
            background: styles.downloadBackground,
            border: `1px solid ${styles.downloadBorder}`,
            borderRadius: 6,
            color: styles.downloadColor,
            cursor: "pointer",
            display: "inline-flex",
            fontSize: 12,
            fontWeight: 700,
            gap: 5,
            minHeight: 28,
            padding: "0.3rem 0.6rem",
          }}
        >
          <Download aria-hidden="true" size={13} />
          <span>{downloadLabel}</span>
        </button>
      </span>
    </div>
  );
}
